"use client"

import React, { useMemo } from "react"
import { removePromoCodeFromCurrentBasketAction } from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { XCircleIcon } from "lucide-react"

import { formatPrice } from "@/lib/helper"
import { getCountry } from "@/lib/locale/locale-common-utils"
import { BasketContext, BasketTotalData } from "@/lib/storefront/basket/basket"
import { determineBasketTotalData } from "@/lib/storefront/basket/basketHelper"
import { Button, Skeleton } from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import BasicCard from "@/components/common/BasicCard"
import { UbBasketSummaryStoryblok } from "@/components/storyblok/component-types"

interface BasketSummaryProps {
  basketContext: BasketContext
  blok: UbBasketSummaryStoryblok
  context: string
  summaryCompact: boolean
}

export default function BasketSummaryClient({ basketContext, blok, context, summaryCompact }: BasketSummaryProps) {
  const isoLocale = basketContext.isoLocale
  const isB2B = basketContext.isB2B
  const isAnonymous = basketContext.isAnonymous

  const { basketData, basketLineItems, basketPromotionCodes, setBasketData } = useUbStorefrontState()

  const basketTotalData: BasketTotalData = useMemo(() => {
    //console.dir(basketData,{depth:5})
    return determineBasketTotalData(isB2B, basketData, basketLineItems, basketPromotionCodes, isAnonymous, isoLocale)
  }, [basketData, basketLineItems, basketPromotionCodes, isB2B, isAnonymous, isoLocale])

  /*  useEffect(() => {
    console.dir(basketTotalData)
    console.dir(basketPromotionCodes)
  }, [basketPromotionCodes, basketTotalData])*/

  const showHandlingFeeHint = useMemo(() => {
    return getCountry(isoLocale) === "de" && basketTotalData && !isB2B && basketTotalData.paymentCostAmountGross > 0
  }, [isoLocale, basketTotalData, isB2B])

  function removePromoCode(promoCode: string) {
    removePromoCodeFromCurrentBasketAction(promoCode).then((result) => {
      setBasketData(result.basketData)
    })
  }

  return (
    <>
      {basketData && basketTotalData.totalProductQuantity > 0 && basketLineItems && basketLineItems.length > 0 ? (
        <div className={"sticky top-[7.5rem] lg:top-[8.5rem]"}>
          <BasicCard
            className={cn("min-h-0 sm:min-h-64", {
              "min-h-24 static border-0": summaryCompact,
              "bg-transparent p-0 sm:p-0 border-none min-h-24 sm:min-h-24 static": context === "light",
            })}
          >
            <pre className={"hidden text-xs"}>{JSON.stringify(basketData, undefined, 2)}</pre>

            <div className={"flex flex-col gap-2"}>
              <div
                className={cn("font-bold", {
                  hidden: summaryCompact,
                })}
              >
                {blok?.label_summary ?? ""}
              </div>

              {basketData && basketTotalData.totalProductQuantity && basketTotalData.totalProductQuantity > 0 && (
                <div className="grid grid-cols-12 text-sm">
                  {basketData.data?.totals?.itemTotal?.net && (
                    <>
                      <span className={"col-span-8 text-nowrap font-bold"}>{blok.label_position_sum_net}</span>
                      <span data-testid={"listItemTotalNet"} className="col-span-4 text-nowrap text-right  font-bold">
                        {formatPrice(isoLocale, basketTotalData.listItemTotalNet, basketTotalData.currency)}
                      </span>
                    </>
                  )}

                  {/*  <span className={"text-nowrap col-span-8 font-bold"}>{blok.label_position_sum_gross}</span>
                    <span data-testid={"listItemTotalGross"} className="text-right text-nowrap col-span-4  font-bold">
                      {formatPrice(isoLocale, basketTotalData.listItemTotalGross, basketTotalData.currency)}
                    </span>
                  */}

                  {basketTotalData.ecoTaxTotal > 0 && (
                    <>
                      <span className={"col-span-8 text-nowrap"}>{blok?.eco_tax_hint ?? ""}</span>
                      <span data-testid={"ecoTaxTotal"} className="col-span-4 text-nowrap text-right">
                        {<>{formatPrice(isoLocale, basketTotalData.ecoTaxTotal, basketTotalData.currency)}</>}
                      </span>
                    </>
                  )}
                  {basketTotalData.totalSavingsGeneralNet && basketTotalData.totalSavingsGeneralNet > 0.009 ? (
                    <>
                      <span className={"col-span-8 text-nowrap"}>{blok.label_general_discount}</span>
                      <span
                        data-testid={"totalSavingsGeneralNet"}
                        className="col-span-4 text-nowrap text-right font-bold text-green-500"
                      >
                        {formatPrice(isoLocale, -1 * basketTotalData.totalSavingsGeneralNet, basketTotalData.currency)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className={"col-span-8 text-nowrap"}></span>
                      <span className={"col-span-4 text-nowrap"}></span>
                    </>
                  )}

                  {basketTotalData.totalSavingsSpecialNet && basketTotalData.totalSavingsSpecialNet > 0.009 ? (
                    <>
                      <span className={"col-span-8 text-nowrap"}>{blok.label_special_discount}</span>
                      <span
                        data-testid={"totalSavingsSpecialNet"}
                        className="col-span-4 text-nowrap text-right font-bold text-green-500"
                      >
                        {formatPrice(isoLocale, -1 * basketTotalData.totalSavingsSpecialNet, basketTotalData.currency)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className={"col-span-8 text-nowrap"}></span>
                      <span className={"col-span-4 text-nowrap"}></span>
                    </>
                  )}

                  {basketTotalData.totalSavingsPriceListNet && basketTotalData.totalSavingsPriceListNet > 0.009 ? (
                    <>
                      <span className={"col-span-8 text-nowrap"}>{blok.label_pricelist_discount}</span>
                      <span
                        data-testid={"totalSavingsPriceListNet"}
                        className="col-span-4 text-nowrap text-right font-bold text-green-500"
                      >
                        {formatPrice(
                          isoLocale,
                          -1 * basketTotalData.totalSavingsPriceListNet,
                          basketTotalData.currency
                        )}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className={"col-span-8 text-nowrap"}></span>
                      <span className={"col-span-4 text-nowrap"}></span>
                    </>
                  )}

                  {basketTotalData.promotionCode && (
                    <>
                      <div className={"col-span-8 flex flex-row items-center gap-1"}>
                        <span className={"col-span-8 text-nowrap"}>
                          {(blok.label_promotion_code ?? "").replace("$promotionCode", basketTotalData.promotionCode)}
                        </span>
                        <Button
                          onClick={() => removePromoCode(basketTotalData.promotionCode ?? "")}
                          variant={"ghost"}
                          size={"tiny"}
                        >
                          <XCircleIcon className={"size-4"} />
                        </Button>
                      </div>
                      <span
                        data-testid={"promotionAmountNet"}
                        className="col-span-4 text-nowrap text-right font-bold text-green-500"
                      >
                        {formatPrice(isoLocale, basketTotalData.promotionAmountNet, basketTotalData.currency)}
                      </span>
                    </>
                  )}

                  {basketTotalData.hasDiscounts && basketData.data?.totals?.grandTotal?.net && (
                    <>
                      <span className={"col-span-8 mt-2 text-nowrap font-bold"}>{blok.label_subtotal_sum_net}</span>
                      <span
                        data-testid={"discountedItemTotalNet"}
                        className="col-span-4 mt-2 text-nowrap text-right font-bold"
                      >
                        {formatPrice(isoLocale, basketTotalData.discountedItemTotalNet, basketTotalData.currency)}
                      </span>
                    </>
                  )}

                  <span className="col-span-8 text-nowrap ">{blok.label_taxtotal_net}</span>
                  <span data-testid={"totalTax"} className="col-span-4 text-nowrap text-right font-bold">
                    {formatPrice(isoLocale, basketTotalData.totalTax, basketTotalData.currency)}
                  </span>

                  {/*Zwischensumme (brutto)*/}
                  {basketTotalData.discountedItemTotalGross > 0.009 && (
                    <>
                      <span className="col-span-8 text-nowrap font-bold">{blok.label_subtotal_sum_gross}</span>
                      <span data-testid={"totalGross"} className="col-span-4 text-nowrap text-right font-bold">
                        {formatPrice(isoLocale, basketTotalData.discountedItemTotalGross, basketTotalData.currency)}
                      </span>
                    </>
                  )}
                  {/*Shipping Cost*/}

                  <span className={"col-span-8 text-nowrap"}>{blok.label_shipping_cost}</span>
                  <span
                    data-testid={"shippingTotalNet"}
                    className="col-span-4 text-nowrap text-right font-bold text-green-500"
                  >
                    {formatPrice(isoLocale, basketTotalData.shippingTotalNet, basketTotalData.currency)}
                  </span>

                  {/*Abwicklungspauschale (Aus payment costs)*/}
                  {!isB2B && basketTotalData.paymentCostAmountGross > 0 && (
                    <>
                      <div className="  col-span-8 text-nowrap ">{blok.label_handling_fee}</div>
                      <span data-testid={"paymentCostAmountGross"} className="  col-span-4 text-nowrap text-right ">
                        {formatPrice(isoLocale, basketTotalData.paymentCostAmountGross, basketTotalData.currency)}
                      </span>
                    </>
                  )}

                  {/*Summe über alles brutto*/}
                  <div className="col-span-8 mt-2 flex items-center text-wrap font-bold">
                    {blok.label_grandtotal_gross}
                  </div>
                  <span
                    data-testid={"grandTotalGross"}
                    className="col-span-4 mt-2 text-nowrap text-right font-bold text-primary sm:text-lg"
                  >
                    {formatPrice(isoLocale, basketTotalData.grandTotalGross, basketTotalData.currency)}
                  </span>

                  {/*Gesamtersparnis (brutto)*/}
                  {basketTotalData.totalSavingsGross < -0.009 && (
                    <>
                      <span className="col-span-8 text-nowrap ">{blok.label_savings_gross}</span>
                      <span
                        data-testid={"totalSavingsGross"}
                        className="col-span-4 text-nowrap text-right font-bold text-green-500"
                      >
                        {formatPrice(isoLocale, basketTotalData.totalSavingsGross, basketTotalData.currency)}
                      </span>
                    </>
                  )}
                </div>
              )}

              {showHandlingFeeHint && (
                <p data-testid={"handlingFeeHint"} className={"text-xs text-gray-400"}>
                  {blok?.handlingFeeHint ?? ""}
                </p>
              )}
            </div>
          </BasicCard>
        </div>
      ) : (
        <Skeleton className="h-64 w-full" />
      )}
    </>
  )
}
